import type { SignupDownloadHandoff } from '../services/signup-api/signup-api';

/**
 * The campaign values a store link carries. The stored landing attribution
 * record satisfies it as is; only the five UTM fields are read from it.
 * `medium` is the legacy direct-download link's own `?medium=`, which the SMS
 * links carry instead of a UTM.
 */
export interface DownloadCampaign {
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  medium?: string | null;
}

/**
 * Where the member met the store links, named in the campaign token the way
 * the legacy signup names it, so installs from each place are told apart: the
 * end of the flow, the direct-download link's page, or that link's jump
 * straight to a phone's store.
 */
export type DownloadPlacement = 'download-page' | 'download-link-page' | 'download-link-redirect';

// The Pelago store listings. The Galapagos member app ships to members as an
// in-place update of these same listings, so the links do not change with it.
const APP_STORE_URL = 'https://apps.apple.com/us/app/pelago-health/id1234288038';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details';
const PLAY_PACKAGE = 'co.digithera.v2.quitgenius';

// App Store Connect campaign analytics: Pelago's provider token and the
// software media type, as the legacy signup sends them.
const APPLE_PROVIDER_TOKEN = '118330298';
const APPLE_MEDIA_TYPE = '8';

/**
 * The legacy signup's direct-download link. On a phone it redirects to that
 * phone's store; on a computer it shows a download page. It is what the QR
 * encodes until this flow has a direct-download entry of its own.
 */
const SCAN_BASE_URL = 'https://signup.pelagohealth.com/';
const SCAN_SOURCE = 'qr-code';
const SCAN_CTA_SOURCE = 'web-reg-end-download-page';

function present(value: string | null | undefined): value is string {
  return value !== undefined && value !== null && value.length > 0;
}

/**
 * Apple's `ct`: `page*<placement>` then `.medium*`, `.campaign*`, `.source*`
 * for the values present. The UTM medium wins over the link's own `medium`.
 */
function appleCampaignToken(
  campaign: DownloadCampaign | null,
  placement: DownloadPlacement,
): string {
  const segments = [`page*${placement}`];
  const medium = present(campaign?.utmMedium) ? campaign.utmMedium : campaign?.medium;
  if (present(medium)) segments.push(`medium*${medium}`);
  if (present(campaign?.utmCampaign)) segments.push(`campaign*${campaign.utmCampaign}`);
  if (present(campaign?.utmSource)) segments.push(`source*${campaign.utmSource}`);
  return segments.join('.');
}

/** Play's install referrer: the placement, then each present UTM pair, then the link's own `medium`. */
function playReferrer(campaign: DownloadCampaign | null, placement: DownloadPlacement): string {
  const referrer = new URLSearchParams({ page: placement });
  const pairs: [string, string | null | undefined][] = [
    ['utm_source', campaign?.utmSource],
    ['utm_medium', campaign?.utmMedium],
    ['utm_campaign', campaign?.utmCampaign],
    ['utm_content', campaign?.utmContent],
    ['utm_term', campaign?.utmTerm],
    ['medium', campaign?.medium],
  ];
  for (const [key, value] of pairs) {
    if (present(value)) referrer.append(key, value);
  }
  return referrer.toString();
}

function withQuery(base: string, query: URLSearchParams): string {
  return `${base}?${query.toString()}`;
}

/**
 * The download page's three links, built from the member's campaign the way
 * the legacy signup builds them, and labelled with where they are shown.
 * Every value goes through `URLSearchParams`, so nothing a campaign label holds
 * can break the query. This is the one place store destinations come from.
 */
export function buildDownloadHandoff(
  campaign: DownloadCampaign | null,
  placement: DownloadPlacement = 'download-page',
): SignupDownloadHandoff {
  const appStoreUrl = withQuery(
    APP_STORE_URL,
    new URLSearchParams({
      pt: APPLE_PROVIDER_TOKEN,
      mt: APPLE_MEDIA_TYPE,
      ct: appleCampaignToken(campaign, placement),
    }),
  );
  const playStoreUrl = withQuery(
    PLAY_STORE_URL,
    new URLSearchParams({ id: PLAY_PACKAGE, referrer: playReferrer(campaign, placement) }),
  );
  const scanUrl = withQuery(
    SCAN_BASE_URL,
    new URLSearchParams({ download: '', source: SCAN_SOURCE, cta_source: SCAN_CTA_SOURCE }),
  );
  return { appStoreUrl, playStoreUrl, scanUrl };
}
